import { type PropsWithChildren, type ReactNode, useEffect, useState } from 'react';
import { Layout, Menu, Typography, Button, Tag, Space, Popconfirm, Tooltip, App as AntApp } from 'antd';
import type { MenuProps } from 'antd';
import {
  LogoutOutlined,
  HomeOutlined,
  TeamOutlined,
  BarChartOutlined,
  SafetyOutlined,
  UserOutlined,
  KeyOutlined,
  AppstoreOutlined,
  DatabaseOutlined,
  DesktopOutlined,
  CloudUploadOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { SessionUser } from '@/services/session';
import { logout } from '@/services/session';
import LanguageSwitcher from '@/components/ui/LanguageSwitcher';

function formatCountdown(toMs: number): string {
  const s = Math.max(0, Math.floor(toMs / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    return `${h}h${m % 60}m`;
  }
  return `${m}m${r.toString().padStart(2, '0')}s`;
}

function useTick(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

interface MenuItem {
  key: string;
  label: string;
  icon: ReactNode;
}

function menuForRole(user: SessionUser, t: (key: string) => string): MenuItem[] {
  if (user.organizationType === 'system') {
    return [
      { key: '/admin', label: t('portal.overview'), icon: <HomeOutlined /> },
      { key: '/admin/users', label: t('portal.users'), icon: <TeamOutlined /> },
      { key: '/admin/licenses', label: t('portal.licenses'), icon: <AppstoreOutlined /> },
      { key: '/admin/license-units', label: t('portal.units'), icon: <DatabaseOutlined /> },
      { key: '/admin/devices', label: t('portal.devices'), icon: <DesktopOutlined /> },
      { key: '/admin/p12-certs', label: t('portal.certs'), icon: <KeyOutlined /> },
      { key: '/admin/reports', label: t('portal.reports'), icon: <BarChartOutlined /> },
      { key: '/admin/audit', label: t('portal.audit'), icon: <SafetyOutlined /> },
    ];
  }
  if (user.organizationType === 'distributor') {
    return [
      { key: '/distributor', label: t('portal.overview'), icon: <HomeOutlined /> },
      { key: '/distributor/reports', label: t('portal.reports'), icon: <BarChartOutlined /> },
    ];
  }
  return [
    { key: '/vendor', label: t('portal.licenses'), icon: <HomeOutlined /> },
    { key: '/vendor/bulk-issue', label: t('portal.bulkIssue'), icon: <CloudUploadOutlined /> },
    { key: '/vendor/reports', label: t('portal.reports'), icon: <BarChartOutlined /> },
    { key: '/vendor/account', label: t('portal.account'), icon: <UserOutlined /> },
  ];
}

function orgTag(t: SessionUser['organizationType']): ReactNode {
  switch (t) {
    case 'system':
      return <Tag color="red">SYSTEM</Tag>;
    case 'distributor':
      return <Tag color="geekblue">DISTRIBUTOR</Tag>;
    case 'vendor':
      return <Tag color="green">VENDOR</Tag>;
  }
}

interface Props {
  user: SessionUser;
}

export default function SidebarLayout({ user, children }: PropsWithChildren<Props>) {
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = AntApp.useApp();
  const { t } = useTranslation();
  const items = menuForRole(user, t);
  const now = useTick(1000);

  const sessionExpires = user.expiresAt ? new Date(user.expiresAt).getTime() : null;
  const sudoExpires = user.sudoActive && user.sudoUntil ? new Date(user.sudoUntil).getTime() : null;
  const sessionLeft = sessionExpires != null ? sessionExpires - now : null;
  const sudoLeft = sudoExpires != null ? sudoExpires - now : null;

  const selectedKey =
    items
      .map((i) => i.key)
      .sort((a, b) => b.length - a.length)
      .find((k) => location.pathname === k || location.pathname.startsWith(k + '/')) ??
    items[0]?.key;

  const handleLogout = async () => {
    try {
      await logout();
      message.success('Logged out');
    } catch {
      // ignore
    }
    navigate('/login', { replace: true });
  };

  const onMenuClick: MenuProps['onClick'] = (e) => navigate(e.key);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider width={220} theme="light" style={{ borderRight: '1px solid #f0f0f0' }}>
        <div style={{ padding: '20px 16px', borderBottom: '1px solid #f0f0f0' }}>
          <Title level={5} style={{ margin: 0 }}>
            AVIS
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            License Server
          </Text>
          <div style={{ marginTop: 8 }}>{orgTag(user.organizationType)}</div>
        </div>
        <Menu
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : []}
          onClick={onMenuClick}
          items={items.map((i) => ({ key: i.key, label: i.label, icon: i.icon }))}
          style={{ borderRight: 0 }}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            background: '#fff',
            padding: '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <Space>
            <Text strong>{user.displayName || user.commonName}</Text>
            <Text type="secondary">({user.role})</Text>
            {sudoLeft != null && sudoLeft > 0 && (
              <Tooltip title="Sudo remaining">
                <Tag color="orange">SUDO {formatCountdown(sudoLeft)}</Tag>
              </Tooltip>
            )}
            {sessionLeft != null && sessionLeft > 0 && (
              <Tooltip title="Session idle timeout — resets on activity">
                <Tag color={sessionLeft < 5 * 60_000 ? 'red' : 'default'}>
                  Session {formatCountdown(sessionLeft)}
                </Tag>
              </Tooltip>
            )}
          </Space>
          <LanguageSwitcher />
          <Popconfirm title="Logout?" onConfirm={handleLogout} okText="Yes" cancelText="No">
            <Button icon={<LogoutOutlined />}>Logout</Button>
          </Popconfirm>
        </Header>
        <Content style={{ padding: 24 }}>{children}</Content>
      </Layout>
    </Layout>
  );
}
